import {
	Avatar,
	Box,
	Button,
	Flex,
	Input,
	InputGroup,
	InputLeftElement,
	Select,
	Table,
	TableCaption,
	Tbody,
	Td,
	Th,
	Thead,
	Tr,
	useDisclosure,
	useToast
} from '@chakra-ui/react';
import DashLayout from '../../components/DashLayout';
import { EmptyState } from '../../components/EmptyState';
import { PageHeader } from '../../components/PageHeader';
import { Loading } from '../../components/Loading';
import { StatusAulaBadge } from '../../components/StatusAula';
import { FaCalendarAlt, FaPlus, FaSearch } from 'react-icons/fa';
import { useCallback, useEffect, useState } from 'react';
import { listarAlunos, listarAulasPorProfessorId, listarProfessores } from '../../services/api';
import { ProfessoresGet } from '../Professores';
import { DrawerCreate } from './DrawerCreate';
import { AulaDrawerProps, DrawerUpdate } from './DrawerUpdate';
import { TipoDocumento } from '../../utils/documento';

export interface AlunosType {
	nome: string;
	sobrenome: string;
	documento: string;
	tipoDocumento: TipoDocumento;
	email: string;
	id: string;
	user: {
		ativo: true;
		avatarUrl: string;
	};
	userId: string;
	professorId:string;
}
interface AulasGet {
	id: string;
	titulo: string;
	data: string;
	alunoId: string;
	professorId: string;
	materialId: string;
	status: string;
	aluno: {
		nome: string;
		avatarUrl: string;
		sobrenome: string;
		email: string;
		userId: string;
	};
}

const professorDefault:ProfessoresGet = {
	documento:'',
	tipoDocumento:'CPF',
	email:'',
	id:'',
	nome:'',
	sobrenome:'',
	user:{
		ativo:true,
		avatarUrl:''
	},
	userId:''
}

const alunoDefault:AlunosType = {
	documento:'',
	tipoDocumento:'CPF',
	email:'',
	id:'',
	nome:'',
	sobrenome:'',
	user:{
		ativo:true,
		avatarUrl:''
	},
	userId:'',
	professorId:''
}

const aulaDefault:AulaDrawerProps = {
	id:''
,	alunoId:'',
	data:'',
	materialId:'',
	professorId:'',
	status:'',
	titulo:''
}

export const Aulas = () => {
	const [ aulas, setAulas ] = useState<AulasGet[]>([]);
	const [ professores, setProfessores ] = useState<ProfessoresGet[]>([]);
	const [ professorId, setProfessorId ] = useState<string>('');
	const [ search, setSearch ] = useState<string>('');
	const [ professor, setProfessor ] = useState<ProfessoresGet>(professorDefault);
	const [ aluno, setAluno ] = useState<AlunosType>(alunoDefault);
	const [ aulaSelected, setAulaSelected ] = useState<AulaDrawerProps>(aulaDefault);
	const [ alunos, setAlunos ] = useState<AlunosType[]>([]);
	const [ alunosProfessor, setAlunosProfessor ] = useState<AlunosType[]>([]);
	const [ loading, setLoading ] = useState<boolean>(true);
	// So pede recarga depois que um drawer fecha. Nascer `true` fazia a primeira
	// escolha de professor buscar as aulas duas vezes: uma no handleSelectProfessor
	// e outra no efeito abaixo.
	const [ updateData, setUpdateData ] = useState<boolean>(false);

	const toast = useToast();
	const createDisclosure = useDisclosure();
	const updateDisclosure = useDisclosure();

	const buscarAulas = useCallback((id: string) => {
		setLoading(true);
		listarAulasPorProfessorId(id)
			.then((response) => {
				setLoading(false);
				setAulas(response.data);
				setUpdateData(false);
			})
			.catch((error) => {
				if (!error.response) return;
				setLoading(false);
				toast({
					title: `Não foi possível listar alunos.`,
					status: 'error',
					isClosable: true
				});
			});
	}, [ toast ]);

	const buscarProfessores = useCallback(
		() => {
			setLoading(true);
			listarProfessores()
				.then((response) => {
					setLoading(false);
					setProfessores(response.data);
				})
				.catch((error) => {
					if (!error.response) return;
					setLoading(false);
					toast({
						title: `Não foi possível listar alunos.`,
						status: 'error',
						isClosable: true
					});
				});
		},
		[ toast ]
	);

	const buscarAlunos = useCallback(
		() => {
			setLoading(true);
			listarAlunos()
				.then((response) => {
					setLoading(false);
					setAlunos(response.data);
				})
				.catch((error) => {
					if (!error.response) return;
					setLoading(false);
					toast({
						title: `Não foi possível listar alunos.`,
						status: 'error',
						isClosable: true
					});
				});
		},
		[ toast ]
	);

	useEffect(() => {
		buscarProfessores();
		buscarAlunos();
	}, [ buscarAlunos, buscarProfessores ]);

	useEffect(
		() => {
			if (updateData && professorId) buscarAulas(professorId);
		},
		[ updateData, buscarAulas, professorId ]
	);

	const handleSelectProfessor = (id: string) => {
		setProfessorId(id);
		// Voltar ao placeholder limpa a lista em vez de buscar por id vazio, que
		// nao casa com nenhuma rota do backend - o mesmo que a tela de Alunos faz.
		if (!id) {
			setAulas([]);
			setProfessor(professorDefault);
			setAlunosProfessor([]);
			return;
		}
		buscarAulas(id);
		var profe = professores.find(prof => prof.id === id)
		var alunosFilter = alunos.filter(aln => aln.professorId === id)
		
		if(profe)
			setProfessor(profe);

		if(alunosFilter)
			setAlunosProfessor(alunosFilter);

	};

	return (
		<DashLayout>
			<Flex width="100%" height="100%" flexDir="column">
				<PageHeader title="Aulas">
					<Flex gap="20px" flexWrap="wrap">
						<Select
							onChange={(e) => {
								handleSelectProfessor(e.target.value);
							}}
							placeholder="Selecione o Professor"
							value={professorId}
							borderColor="mist"
							bgColor="paper"
							width="35%"
						>
							{professores.map((item) =>
								<option
									value={item.id}
									key={item.id}
									style={{ color: 'black' }}
								>{`${item.nome} ${item.sobrenome}`}</option>
							)}
						</Select>
						<InputGroup gap={'20px'}>
							<InputLeftElement pointerEvents="none" color={'gray.600'}>
								<FaSearch />
							</InputLeftElement>
							<Input
								type="tel"
								placeholder="Buscar aulas..."
								onChange={(e) => setSearch(e.target.value)}
								value={search}
								borderColor="mist"
								bgColor="paper"
							/>
							<Button leftIcon={<FaPlus />} colorScheme="blue" variant="solid" onClick={()=>{
								createDisclosure.onOpen();
							}}>
								Novo
							</Button>
						</InputGroup>
					</Flex>
				</PageHeader>
				<Flex width="100%" bgColor="white" height="100%">
					{!loading ? (
						aulas.length === 0 ? (
						// O admin so ve aulas depois de escolher um professor; ate la a
						// lista vazia nao quer dizer que a escola nao tem aula nenhuma.
						!professorId ? (
						<EmptyState
							icon={FaCalendarAlt}
							title="Selecione um professor"
							description="A lista de aulas é filtrada por professor. Escolha um no campo acima para ver as aulas dele."
						/>
						) : (
						<EmptyState
							icon={FaCalendarAlt}
							title="Nenhuma aula com este professor ainda"
							description="Agende a primeira aula para ela aparecer aqui e no portal do aluno."
							actionLabel="Nova aula"
							onAction={() => createDisclosure.onOpen()}
						/>
						)
						) : (
						<Box overflowY="auto" overflowX="auto" width={{base:"80vw", md:"100vw", sm:"100vw"}} paddingBottom="200px">
							<Table variant="simple">
								<TableCaption>Aulas</TableCaption>
								<Thead position="sticky" top={0} bgColor="white" zIndex="999">
									<Tr>
										<Th>Título</Th>
										<Th>Data</Th>
										<Th>Aluno</Th>
										<Th>Professor</Th>
										<Th>Status</Th>
									</Tr>
								</Thead>
								<Tbody>
									{aulas
									.sort((a, b) => {
										if (a.data > b.data) return -1;
										if (a.data < b.data) return 1;
										return 0;
									})
									.map((aula, index) => {
										if (
											!Object.values(aula)
												.map((variavel) => variavel)
												.reduce((a, b) => (b = a + ' ' + b))
												.toLowerCase()
												.includes(search.toLowerCase())
										)
											return null;

										return(<Tr
											key={index}
											_hover={{ bgColor: "gray.100", cursor: "pointer" }}
											onClick={()=>{
												var alunoSelected = alunos.find(aln => aln.id === aula.alunoId);
												alunoSelected && setAluno(alunoSelected);
												setAulaSelected(aula);
												if(aula.status === "AGENDADA")
													updateDisclosure.onOpen();
											}}
									  >
										<Td
										>
										  {aula.titulo}
										</Td>
										<Td
											onClick={()=>{
												var alunoSelected = alunos.find(aln => aln.id === aula.alunoId);
												alunoSelected && setAluno(alunoSelected);
												setAulaSelected(aula);
												if(aula.status === "AGENDADA")
													updateDisclosure.onOpen();
											}}
										>
										  {new Date(aula.data).toLocaleString("pt-br", {
											day: "2-digit",
											month: "2-digit",
											year: "2-digit",
										  })}
										</Td>
										<Td
											onClick={()=>{
												var alunoSelected = alunos.find(aln => aln.id === aula.alunoId);
												alunoSelected && setAluno(alunoSelected);
												setAulaSelected(aula);
												if(aula.status === "AGENDADA")
													updateDisclosure.onOpen();
											}}
										>
										  <Flex gap={"10px"} alignItems={"center"}>
											<Avatar
											  size="sm"
											  name={aula.aluno.nome}
											  src={aula.aluno.avatarUrl}
											/>
											{aula.aluno.nome}
										  </Flex>
										</Td>
										<Td
											onClick={()=>{
												var alunoSelected = alunos.find(aln => aln.id === aula.alunoId);
												alunoSelected && setAluno(alunoSelected);
												setAulaSelected(aula);
												if(aula.status === "AGENDADA")
													updateDisclosure.onOpen();
											}}
										>
										  {professor ? (
											<Flex gap={"10px"} alignItems={"center"}>
											  <Avatar
												size="sm"
												name={professor?.nome}
												src={professor.user.avatarUrl}
											  />
											  {`${professor.nome} ${professor.sobrenome}`}
											</Flex>
										  ) : (
											<Flex></Flex>
										  )}
										</Td>
										<Td 
											onClick={()=>{
												var alunoSelected = alunos.find(aln => aln.id === aula.alunoId);
												alunoSelected && setAluno(alunoSelected);
												setAulaSelected(aula);
												if(aula.status !== "CONCLUIDA")
													updateDisclosure.onOpen();
											}}
										>
										  <StatusAulaBadge status={aula.status} />
										</Td>
									  </Tr>)
									})}
								</Tbody>
							</Table>
						</Box>
						)
					) : (
						<Loading />
					)}
				</Flex>
			</Flex>
			<DrawerCreate 
				isOpen={createDisclosure.isOpen} 
				onClose={createDisclosure.onClose} 
				onOpen={createDisclosure.onOpen} 
				afterClose={()=>setUpdateData(true)} 
				alunos={alunosProfessor}
				professor={professor}
			/>
			<DrawerUpdate
				isOpen={updateDisclosure.isOpen} 
				onClose={updateDisclosure.onClose} 
				onOpen={updateDisclosure.onOpen} 
				afterClose={()=>setUpdateData(true)} 
				professor={professor}
				aluno={aluno}
				aula={aulaSelected}
			/>
		</DashLayout>
	);
};
